'use client';

import * as React from 'react';
import { ArrowUpDown } from 'lucide-react';

import { cn } from '@repo/ui/lib/utils';
import type { SortDirection } from '@repo/ui/lib/table-sort';
import { LiquidSurface } from './liquid-surface';
import { TableHead } from './table';

/**
 * Card chrome for a data table — the v2 glass-panel surface every data
 * table (Portfolio positions, Vaults, Risk & Liquidity, Activity, Reports)
 * wraps its `<Table>` in, replacing each feature's own ad hoc
 * `border-border bg-card rounded-xl overflow-hidden` div. Compose the plain
 * `Table`/`TableHeader`/`TableBody`/`TableRow`/`TableCell` primitives from
 * `@repo/ui/components/table` inside it — those already accept any content
 * per cell (a `LimitProgressBar`, a `Badge`, plain text, ...), so this shell
 * only standardizes the surface and header styling around them.
 */
function TableShell({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <LiquidSurface
      type="panel"
      data-slot="table-shell"
      className={cn('overflow-hidden p-0', className)}
      {...props}
    >
      {children}
    </LiquidSurface>
  );
}

/** Header bar above the table — title/count on the left, actions/toggles on the right. */
function TableShellToolbar({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-shell-toolbar"
      className={cn(
        'border-rowa-border-default flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface TableShellHeadProps extends React.ComponentProps<'th'> {
  align?: 'left' | 'right';
  /** Omit for a non-sortable column header. */
  sortDirection?: SortDirection | null;
  onSort?: () => void;
}

/** A `TableHead` styled to the v2 label typography, with an optional sort affordance. */
function TableShellHead({
  align = 'left',
  sortDirection,
  onSort,
  className,
  children,
  ...props
}: TableShellHeadProps) {
  const isSortable = !!onSort;

  return (
    <TableHead
      className={cn(
        'text-rowa-label-sm text-rowa-text-secondary py-3 tracking-[0.06em] uppercase',
        align === 'right' && 'text-right',
        isSortable && 'hover:text-rowa-text-primary cursor-pointer transition-colors select-none',
        className
      )}
      onClick={onSort}
      {...props}
    >
      <span className={cn('flex items-center gap-1.5', align === 'right' && 'justify-end')}>
        {children}
        {isSortable && (
          <ArrowUpDown
            className={cn('h-3 w-3', sortDirection ? 'opacity-100' : 'opacity-40')}
            aria-hidden
          />
        )}
      </span>
    </TableHead>
  );
}

export { TableShell, TableShellToolbar, TableShellHead };
export type { TableShellHeadProps };
